import { useEffect, useMemo, useRef, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok, formatRaidHpInput, raidHpValueFromInput } from '../../../shared/domain/raidHpFormat.js';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { useRaidRoomPresence } from '../../raid-control/hooks/useRaidRoomPresence.js';
import { RaidPracticeDeckDisplay, useSharedNikkeCatalog } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import { useUnionRoster } from '../../union-roster/hooks/useUnionRoster.js';
import { buildRaidFullSimulationInput } from '../domain/raidFullSimulationPlan.js';
import { buildSwapScenarioInput, listSwapCandidates, summarizeTradeoff } from '../domain/raidFullSimulationTradeoff.js';
import { RaidTetrisFullTradeoffDialog } from './RaidTetrisFullTradeoffDialog.jsx';

function formatEok(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function itemLabel(item = {}) {
  return String(item.title || '').trim() || `모의전 ${Number(item.slotNo || 1)}`;
}

function memberOpenid(value = {}) {
  return String(value.openid || value.memberOpenid || value.member_openid || value.memberKey || '').trim();
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function timeMinutes(value) {
  const match = String(value || '').trim().match(/^(\d{2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 ? hour * 60 + minute : null;
}

function currentTimeText() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function availabilityWindows(value = {}) {
  const source = value.availabilityWindows ?? value.availability_windows ?? [];
  if (Array.isArray(source) && source.length) {
    return source.map((item) => ({
      start: String(item?.start ?? item?.start_at ?? '').slice(0, 5),
      end: String(item?.end ?? item?.end_at ?? '').slice(0, 5),
    })).filter((item) => timeMinutes(item.start) !== null && timeMinutes(item.end) !== null && item.start !== item.end);
  }
  const legacy = String(value.availableAt ?? value.available_at ?? '').slice(0, 5);
  const start = timeMinutes(legacy);
  if (start === null) return [];
  const end = (start + 60) % 1440;
  return [{ start: legacy, end: `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}` }];
}

function availableAt(value, referenceTime) {
  const current = timeMinutes(referenceTime);
  if (current === null) return false;
  return availabilityWindows(value).some((window) => {
    const start = timeMinutes(window.start);
    const end = timeMinutes(window.end);
    if (start === null || end === null) return false;
    return start < end ? current >= start && current < end : current >= start || current < end;
  });
}

function buildCoverageRows({ bosses = [], targets = [], candidates = [] }) {
  const poolMemberKeys = new Set(candidates.map(memberOpenid).filter(Boolean));
  return (Array.isArray(bosses) ? bosses : []).map((boss) => {
    const bossNo = Number(boss.bossNo || 0);
    const rows = candidates.filter((candidate) => Number(candidate.bossNo) === bossNo);
    const bestByMember = new Map();
    for (const candidate of rows) {
      const key = memberOpenid(candidate);
      if (!key) continue;
      const damage = numberValue(candidate.damageEok ?? candidate.damage);
      if (damage > (bestByMember.get(key) || 0)) bestByMember.set(key, damage);
    }
    const nextTarget = targets.find((target) => Number(target.bossNo) === bossNo && numberValue(target.remaining) > 0) || null;
    const representativeTotal = [...bestByMember.values()].reduce((sum, damage) => sum + damage, 0);
    const shortage = nextTarget ? Math.max(0, numberValue(nextTarget.remaining) - representativeTotal) : 0;
    return {
      boss,
      bossNo,
      slotCount: rows.length,
      memberCount: bestByMember.size,
      poolMemberCount: poolMemberKeys.size,
      missingMemberCount: Math.max(0, poolMemberKeys.size - bestByMember.size),
      representativeTotal,
      nextTarget,
      shortage,
      status: !rows.length ? 'empty' : shortage > 0 ? 'lack' : 'ready',
    };
  });
}

function SimulationState({ mobile, error = false, title, description, onRetry = null }) {
  return (
    <section className={`raid-tetris-full-simulation ${mobile ? 'mobile' : 'desktop'}`} aria-label={title}>
      <header className="raid-tetris-full-heading">
        <div>
          <p className="section-eyebrow">FULL RAID SIMULATION</p>
          <h3>1~3단 진행 시뮬레이션</h3>
          <span>멤버별 남은 타수와 3타 전체 니케 중복을 확인해 계산합니다.</span>
        </div>
        <strong>{error ? '계산 오류' : '계산 중'}</strong>
      </header>
      <div className={`raid-tetris-full-calculation-state ${error ? 'error' : ''}`} role="status" aria-live="polite">
        {!error ? <span aria-hidden="true" /> : null}
        <strong>{title}</strong>
        <p>{description}</p>
        {onRetry ? <button type="button" onClick={onRetry}>다시 계산</button> : null}
      </div>
    </section>
  );
}

export function RaidTetrisFullSimulation({ season, candidates = [], members = [], mobile = false }) {
  const roster = useUnionRoster();
  const { catalog, loading: catalogLoading, error: catalogError } = useSharedNikkeCatalog();
  const selfUserId = String(roster.members.find((member) => member.isSelf)?.linkedUserId || '').trim();
  const presence = useRaidRoomPresence({ unionId: roster.unionId, seasonId: season?.id });
  const [fitMode, setFitMode] = useState('kill');
  const [allowedErrorEok, setAllowedErrorEok] = useState(0);
  const [useOnlinePresence, setUseOnlinePresence] = useState(false);
  const [useScheduledAvailability, setUseScheduledAvailability] = useState(false);
  const [referenceTime, setReferenceTime] = useState(currentTimeText);
  const [planIndex, setPlanIndex] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const [calculation, setCalculation] = useState({ loading: false, result: null, error: '', durationMs: 0 });
  const [tradeoff, setTradeoff] = useState(null);
  const requestCounter = useRef(0);
  const tradeoffWorkerRef = useRef(null);

  const completedBaseStages = Number(season?.globalPhase || 1) >= 4;
  const sourceMembers = roster.members.length ? roster.members : members;
  const onlineUserIds = useMemo(() => new Set(presence.members.map((member) => String(member.userId || '')).filter(Boolean)), [presence.members]);
  const onlineMemberOpenids = useMemo(() => new Set(sourceMembers
    .filter((member) => onlineUserIds.has(String(member.linkedUserId || '')))
    .map(memberOpenid)
    .filter(Boolean)), [onlineUserIds, sourceMembers]);
  const accessFilterEnabled = useOnlinePresence || useScheduledAvailability;
  const memberMatchesAccess = (member) => (
    (useOnlinePresence && onlineMemberOpenids.has(memberOpenid(member)))
    || (useScheduledAvailability && availableAt(member, referenceTime))
  );
  const simulationCandidates = useMemo(() => (
    accessFilterEnabled ? candidates.filter(memberMatchesAccess) : candidates
  ), [accessFilterEnabled, candidates, onlineMemberOpenids, referenceTime, useOnlinePresence, useScheduledAvailability]);
  const simulationMembers = useMemo(() => (
    accessFilterEnabled ? members.filter(memberMatchesAccess) : members
  ), [accessFilterEnabled, members, onlineMemberOpenids, referenceTime, useOnlinePresence, useScheduledAvailability]);
  const scheduledMemberCount = useMemo(() => new Set(candidates
    .filter((candidate) => availableAt(candidate, referenceTime))
    .map(memberOpenid)
    .filter(Boolean)).size, [candidates, referenceTime]);
  const input = useMemo(() => buildRaidFullSimulationInput({
    season,
    candidates: simulationCandidates,
    members: simulationMembers,
    catalog,
    fitMode,
    allowedErrorEok,
  }), [allowedErrorEok, catalog, fitMode, season, simulationCandidates, simulationMembers]);

  // A full run takes seconds, and upstream hooks (roster/presence/live raid refresh) hand back new
  // object identities on every silent poll even when nothing about the raid actually changed. Key
  // the run on the input's CONTENT instead of its identity so background refreshes don't restart
  // the calculation under the user while they're reading the result.
  const inputSignature = useMemo(() => JSON.stringify({
    fitMode: input.fitMode,
    allowedErrorEok: input.allowedErrorEok,
    memberCount: input.memberCount,
    targets: (input.targets || []).map((target) => `${target.id}:${target.remaining}`),
    candidates: (input.candidates || []).map((candidate) => `${candidate.id}:${candidate.memberKey}:${candidate.damage}:${(candidate.deckKeys || []).join('/')}`),
    usage: [...(input.initialUsage instanceof Map ? input.initialUsage.entries() : [])].map(([key, count]) => `${key}:${count}`).sort(),
    usedDeckKeys: [...(input.initialDeckKeys instanceof Map ? input.initialDeckKeys.entries() : [])].map(([key, values]) => `${key}:${[...values].sort().join('/')}`).sort(),
  }), [input]);

  useEffect(() => {
    if (completedBaseStages || catalogLoading || catalogError) return undefined;
    const requestId = `${Date.now()}:${requestCounter.current + 1}`;
    requestCounter.current += 1;
    const worker = new Worker(new URL('../workers/raidFullSimulatorWorker.js', import.meta.url), { type: 'module' });
    let active = true;
    // Keep the previous result on screen while recalculating so a refresh doesn't blank the board.
    setCalculation((current) => ({ ...current, loading: true, error: '' }));

    worker.addEventListener('message', (event) => {
      if (!active || event.data?.requestId !== requestId) return;
      if (event.data?.error) {
        setCalculation({ loading: false, result: null, error: String(event.data.error), durationMs: 0 });
      } else {
        setCalculation({
          loading: false,
          result: event.data?.result || null,
          error: '',
          durationMs: Number(event.data?.durationMs || 0),
        });
      }
    });
    worker.addEventListener('error', () => {
      if (active) setCalculation({ loading: false, result: null, error: '시뮬레이션 작업자를 실행하지 못했습니다.', durationMs: 0 });
    });
    worker.postMessage({ requestId, input });

    return () => {
      active = false;
      worker.terminate();
    };
  }, [catalogError, catalogLoading, completedBaseStages, inputSignature, retryNonce]);

  const simulation = calculation.result;
  const signature = simulation
    ? `${fitMode}:${allowedErrorEok}:${useOnlinePresence ? 'online' : 'no-online'}:${useScheduledAvailability ? referenceTime : 'no-schedule'}:${simulation.plans.map((plan) => plan.signature).join('|')}`
    : '';
  const plan = simulation?.plans?.[planIndex] || simulation?.best || null;
  const assignmentByTarget = useMemo(() => new Map((plan?.assignments || []).map((assignment) => [assignment.target.id, assignment])), [plan]);
  const orderByTarget = useMemo(() => {
    const map = new Map();
    let order = 0;
    for (const assignment of plan?.assignments || []) {
      if (!assignment.items?.length) continue;
      order += 1;
      map.set(assignment.target.id, order);
    }
    return map;
  }, [plan]);
  const coverageRows = useMemo(() => buildCoverageRows({
    bosses: season?.bosses || [],
    targets: input.targets,
    candidates: simulationCandidates,
  }), [input.targets, season?.bosses, simulationCandidates]);
  const infinityBoss = season?.bosses?.find((boss) => Number(boss.bossNo) === 5) || null;
  const stages = [1, 2, 3];

  const stopTradeoffWorker = () => {
    tradeoffWorkerRef.current?.terminate();
    tradeoffWorkerRef.current = null;
  };

  useEffect(() => setPlanIndex(0), [signature]);
  useEffect(() => {
    stopTradeoffWorker();
    setTradeoff(null);
  }, [signature, planIndex]);
  useEffect(() => stopTradeoffWorker, []);

  const openTradeoff = (targetId, itemId) => setTradeoff({ targetId, itemId, status: 'picking', alternate: null, result: null, error: '' });
  const closeTradeoff = () => {
    stopTradeoffWorker();
    setTradeoff(null);
  };
  const backToTradeoffPicker = () => {
    stopTradeoffWorker();
    setTradeoff((current) => (current ? { ...current, status: 'picking', alternate: null, result: null, error: '' } : current));
  };

  const runTradeoff = (alternate) => {
    if (!tradeoff) return;
    const { targetId, itemId } = tradeoff;
    const scenario = buildSwapScenarioInput({
      input,
      targetId,
      replaceItemId: itemId,
      alternateCandidateId: alternate.id,
    });
    if (!scenario) {
      setTradeoff({ targetId, itemId, status: 'error', alternate, result: null, error: '비교를 계산할 수 없습니다.' });
      return;
    }
    stopTradeoffWorker();
    setTradeoff({ targetId, itemId, status: 'loading', alternate, result: null, error: '' });
    const requestId = `${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const worker = new Worker(new URL('../workers/raidFullSimulatorWorker.js', import.meta.url), { type: 'module' });
    tradeoffWorkerRef.current = worker;
    const settle = (next) => {
      setTradeoff((current) => (current?.targetId === targetId && current?.itemId === itemId && current?.alternate?.id === alternate.id ? { ...current, ...next } : current));
      if (tradeoffWorkerRef.current === worker) tradeoffWorkerRef.current = null;
      worker.terminate();
    };
    worker.addEventListener('message', (event) => {
      if (event.data?.requestId !== requestId) return;
      if (event.data?.error) {
        settle({ status: 'error', result: null, error: String(event.data.error) });
      } else {
        settle({
          status: 'done',
          result: summarizeTradeoff({ baselinePlan: plan, scenarioResult: event.data?.result, applied: scenario.applied, pin: scenario.pin }),
          error: '',
        });
      }
    });
    worker.addEventListener('error', () => settle({ status: 'error', result: null, error: '비교 계산 워커를 실행하지 못했습니다.' }));
    worker.postMessage({ requestId, input: scenario.input });
  };

  if (completedBaseStages) {
    return (
      <section className={`raid-tetris-full-simulation ${mobile ? 'mobile' : 'desktop'}`} aria-label="1단부터 3단까지 완료 내역">
        <header className="raid-tetris-full-heading">
          <div>
            <p className="section-eyebrow">FULL RAID SIMULATION</p>
            <h3>1~3단 전체 완료</h3>
            <span>모든 보스 처치가 끝나 1~3단의 남은 HP 계산이 없습니다.</span>
          </div>
          <strong>3단 완료</strong>
        </header>
        <div className="raid-tetris-full-summary">
          <article><span>현재 대상</span><strong>{bossDisplayName(infinityBoss, 5)}</strong><small>5보스만 활성</small></article>
          <article><span>진행 단계</span><strong>∞</strong><small>4단 인피니티</small></article>
          <article><span>남은 HP</span><strong>없음</strong><small>처치 목표 미사용</small></article>
          <article><span>테트리스</span><strong>임의 목표 딜</strong><small>직접 입력 후 계산</small></article>
        </div>
        <p className="raid-tetris-full-empty">이전 단수의 체력값은 다시 사용하지 않습니다. 아래에서 5보스 임의 목표 딜만 입력해 조합을 계산합니다.</p>
      </section>
    );
  }

  if (catalogLoading) {
    return <SimulationState mobile={mobile} title="니케 식별 정보 불러오는 중…" description="name_code와 캐릭터 ID를 같은 니케로 통합한 뒤 시뮬레이션을 시작합니다." />;
  }

  if (catalogError) {
    return <SimulationState mobile={mobile} error title="니케 식별 정보를 불러오지 못했습니다." description={catalogError} />;
  }

  if (calculation.error) {
    return <SimulationState mobile={mobile} error title="1~3단 계산에 실패했습니다." description={calculation.error} onRetry={() => setRetryNonce((current) => current + 1)} />;
  }

  if (!simulation) {
    return <SimulationState mobile={mobile} title="1~3단 추천 계산 중…" description="전체 보스 순서, 멤버별 남은 타수, 한 멤버의 3타 전체 니케 중복을 계산하고 있습니다." />;
  }

  if (!simulation.targets.length) {
    return <section className={`raid-tetris-full-simulation ${mobile ? 'mobile' : 'desktop'}`}><p className="raid-tetris-full-empty">1~3단 보스 체력 설정이 없습니다.</p></section>;
  }

  return (
    <section className={`raid-tetris-full-simulation ${mobile ? 'mobile' : 'desktop'}`} aria-label="1단부터 3단까지 레이드 시뮬레이션">
      <header className="raid-tetris-full-heading">
        <div>
          <p className="section-eyebrow">FULL RAID SIMULATION</p>
          <h3>1~3단 진행 시뮬레이션</h3>
          <span>운영자가 선택한 후보 안에서 남은 타수와 캐릭터 중복 제한을 이어서 계산합니다.</span>
        </div>
        <strong>{`현재 ${season?.globalPhase || 1}단`}</strong>
      </header>

      <section className="raid-tetris-full-options" aria-label="시뮬레이션 계산 설정">
        <header><div><strong>시뮬레이션 설정</strong><small>일반 단일·전체 추천 설정에는 영향을 주지 않습니다.</small></div><span>{simulation.memberCount}명 · {simulation.candidateCount}슬롯{calculation.durationMs ? ` · ${(calculation.durationMs / 1000).toFixed(1)}초` : ''}{calculation.loading ? ' · 재계산 중…' : ''}</span></header>
        <div className="raid-tetris-full-option-grid">
          <div className="raid-tetris-full-option-group">
            <span>계산 기준</span>
            <div className="raid-tetris-full-mode-buttons" role="radiogroup" aria-label="시뮬레이션 계산 기준">
              <button type="button" className={fitMode === 'nearest' ? 'active' : ''} aria-pressed={fitMode === 'nearest'} onClick={() => setFitMode('nearest')}>오차 최소</button>
              <button type="button" className={fitMode === 'kill' ? 'active' : ''} aria-pressed={fitMode === 'kill'} onClick={() => setFitMode('kill')}>막타 우선</button>
            </div>
          </div>
          <label className="raid-tetris-full-number-option">
            <span>허용 오차(억)</span>
            <input type="number" min="0" step="0.01" value={formatRaidHpInput(allowedErrorEok)} onChange={(event) => setAllowedErrorEok(raidHpValueFromInput(event.target.value))} />
            <small>허용 범위 안에서는 더 적은 타수를 우선 비교합니다.</small>
          </label>
          <label className="raid-tetris-full-availability-option">
            <input type="checkbox" checked={useOnlinePresence} onChange={(event) => setUseOnlinePresence(event.target.checked)} />
            <span><strong>현재 사이트 접속자 반영</strong><small>현재 사이트에 접속 중인 {onlineMemberOpenids.size}명을 후보에 포함합니다.</small></span>
          </label>
          <label className="raid-tetris-full-availability-option">
            <input type="checkbox" checked={useScheduledAvailability} onChange={(event) => setUseScheduledAvailability(event.target.checked)} />
            <span><strong>설정 접속시간 반영</strong><small>기준 시각의 등록 시간대에 해당하는 {scheduledMemberCount}명을 포함합니다.</small></span>
          </label>
          <div className={`raid-tetris-full-time-option ${useScheduledAvailability ? 'active' : ''}`}>
            <label><span>접속시간 기준 시각</span><input type="time" value={referenceTime} disabled={!useScheduledAvailability} onChange={(event) => setReferenceTime(event.target.value)} /></label>
            <button type="button" disabled={!useScheduledAvailability} onClick={() => setReferenceTime(currentTimeText())}>현재 시각</button>
          </div>
        </div>
        {accessFilterEnabled ? <p className="raid-tetris-full-access-summary">선택한 접속 출처 중 하나에 해당하면 후보로 사용합니다. 멤버·슬롯 수동 제외 상태가 항상 우선합니다.</p> : null}
      </section>

      <section className="raid-tetris-full-coverage" aria-label="보스별 모의전 입력 현황">
        <header><div><strong>보스별 모의전 현황</strong><small>현재 선택된 후보 기준으로 어느 보스의 모의전 값이 부족한지 확인합니다.</small></div></header>
        <div>
          {coverageRows.map((row) => (
            <article className={row.status} key={row.bossNo}>
              <header><strong>{bossDisplayName(row.boss, row.bossNo)}</strong><ElementIcon element={row.boss.element} /></header>
              <p><span>입력 후보</span><b>{row.memberCount}명 · {row.slotCount}슬롯</b></p>
              <p><span>이 보스 후보 없음</span><b>{row.missingMemberCount}명</b></p>
              <p><span>멤버별 최고합</span><b>{formatEok(row.representativeTotal)}</b></p>
              {row.nextTarget ? <footer><span>다음 목표 {formatRaidHpEok(row.nextTarget.remaining)}</span><strong>{row.shortage > 0 ? `부족 ${formatRaidHpEok(row.shortage)}` : '목표 이상'}</strong></footer> : <footer><span>남은 목표 없음</span><strong>완료</strong></footer>}
            </article>
          ))}
        </div>
      </section>

      {simulation.plans.length > 1 ? (
        <div className="raid-tetris-full-plan-tabs" role="tablist" aria-label="전체 레이드 대체 계획">
          {simulation.plans.map((item, index) => (
            <button className={planIndex === index ? 'active' : ''} type="button" role="tab" aria-selected={planIndex === index} onClick={() => setPlanIndex(index)} key={item.signature || index}>
              <strong>{index === 0 ? 'BEST' : `#${index + 1}`}</strong>
              <small>{item.clearedCount}/{item.totalTargets}개 처치 · {item.usedHits}타</small>
            </button>
          ))}
        </div>
      ) : null}

      {plan ? (
        <>
          <div className="raid-tetris-full-summary">
            <article><span>처치 진행</span><strong>{plan.clearedCount}/{plan.totalTargets}</strong><small>{plan.complete ? '1~3단 완료 가능' : `${plan.firstUnclearedTarget?.level || '-'}단 ${bossDisplayName(plan.firstUnclearedTarget)}에서 중단`}</small></article>
            <article><span>사용 타수</span><strong>{plan.usedHits}타</strong><small>남은 {plan.remainingHits}타</small></article>
            <article><span>추천 총딜</span><strong>{formatEok(plan.totalDamage)}</strong><small>초과 {formatRaidHpEok(plan.totalOverkill)}</small></article>
            <article><span>후보</span><strong>{simulation.memberCount}명</strong><small>{simulation.candidateCount}개 슬롯</small></article>
          </div>

          <div className="raid-tetris-full-stages">
            {stages.map((level) => (
              <section className="raid-tetris-full-stage" key={level}>
                <header><h4>{level}단</h4><span>{simulation.targets.filter((target) => target.level === level && target.remaining <= 0).length}/5 실전 반영 완료</span></header>
                <div className="raid-tetris-full-target-grid">
                  {simulation.targets.filter((target) => target.level === level).map((target) => {
                    const assignment = assignmentByTarget.get(target.id);
                    const cleared = Boolean(assignment?.cleared || target.remaining <= 0);
                    return (
                      <article className={`raid-tetris-full-target ${cleared ? 'cleared' : assignment ? 'blocked' : 'pending'}`} key={target.id}>
                        <header>
                          <strong>{bossDisplayName(target)}</strong>
                          <ElementIcon element={target.element} className="raid-tetris-full-target-element" />
                        </header>
                        <div className="raid-tetris-full-hp"><span>목표 HP</span><strong>{formatRaidHpEok(target.remaining)}</strong><small>전체 {formatRaidHpEok(target.hp)}</small></div>
                        {orderByTarget.has(target.id) ? <p className="raid-tetris-full-target-order">이번 계산에서 {orderByTarget.get(target.id)}번째로 배정됨</p> : null}
                        {target.remaining <= 0 ? (
                          <p className="raid-tetris-full-cleared">실전에서 이미 완료</p>
                        ) : assignment?.items?.length ? (
                          <>
                            <div className="raid-tetris-full-assignment-list">
                              {assignment.items.map((item, index) => (
                                <article key={item.id}>
                                  <header><span>{index + 1}타</span><div><strong>{item.nickname}</strong><small>{itemLabel(item)}</small></div><b>{formatEok(item.damage)}</b></header>
                                  <RaidPracticeDeckDisplay deck={item.deck} mobile={mobile} />
                                  <button type="button" className="raid-tetris-full-tradeoff-toggle" onClick={() => openTradeoff(target.id, item.id)}>이 배정 비교</button>
                                </article>
                              ))}
                            </div>
                            <footer><strong>합계 {formatEok(assignment.total)}</strong><small>{assignment.cleared ? `초과 ${formatRaidHpEok(assignment.overkill)}` : `부족 ${formatRaidHpEok(assignment.remaining)}`}</small></footer>
                          </>
                        ) : (
                          <p className="raid-tetris-full-pending">앞 단계 진행 결과에 따라 대기</p>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          {plan.memberPlans.length ? (
            <section className="raid-tetris-full-member-plans">
              <header><h4>멤버별 예정 타수</h4><span>실전에서 이미 사용한 타수와 니케를 제외한 결과</span></header>
              <div>
                {plan.memberPlans.map((member) => (
                  <article key={member.memberKey}>
                    <header><strong>{member.nickname}</strong><span>{member.items.length}타 · {formatEok(member.total)}</span></header>
                    <div>{member.items.map((item) => <p key={`${item.id}:${item.level}`}><span>{item.level}단 {bossDisplayName(item)}</span><strong>{itemLabel(item)}</strong><b>{formatEok(item.damage)}</b></p>)}</div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : <p className="raid-tetris-full-empty">현재 후보로 계산 가능한 진행 계획이 없습니다.</p>}

      <RaidTetrisFullTradeoffDialog
        mobile={mobile}
        target={tradeoff ? simulation.targets.find((target) => target.id === tradeoff.targetId) || null : null}
        originalItem={tradeoff ? (assignmentByTarget.get(tradeoff.targetId)?.items || []).find((item) => item.id === tradeoff.itemId) || null : null}
        candidates={tradeoff ? listSwapCandidates({ input, plan, targetId: tradeoff.targetId }) : []}
        tradeoff={tradeoff}
        onPick={runTradeoff}
        onBack={backToTradeoffPicker}
        onClose={closeTradeoff}
      />
    </section>
  );
}
